"use client";

/**
 * The app's overlay shell.
 *
 * Every full-screen sheet, dialog and lightbox goes through here so
 * they all behave the same way (owner, 2026-09-22: "I like the new
 * overlay, just make sure that every other place uses the same
 * overlay"):
 *
 *   - rendered into <body> through a portal, because z-index only
 *     competes inside a stacking context and the page almost always
 *     opens one - that is how a z-[2000] sheet ended up under a z-50
 *     header;
 *   - a backdrop that takes the pointer events the page behind should
 *     not receive;
 *   - hardware Back closes it instead of leaving the page;
 *   - Escape closes it, and the body stops scrolling while it is open.
 *
 * It renders nothing of its own chrome: the caller owns the panel.
 */
import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useBackToClose } from "@/lib/use-back-to-close";

export const OVERLAY_Z = 2000;

export default function Overlay({
  open,
  onClose,
  children,
  className = "",
  backdropClassName = "bg-black/50 backdrop-blur-sm",
  closeOnBackdrop = true,
  lockScroll = true,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Layout classes for the full-screen container (flex, padding). */
  className?: string;
  backdropClassName?: string;
  closeOnBackdrop?: boolean;
  lockScroll?: boolean;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useBackToClose(open, onClose);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !lockScroll) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open, lockScroll]);

  if (!open || !mounted) return null;

  return createPortal(
    <div
      className={`fixed inset-0 ${backdropClassName} ${className}`}
      style={{ zIndex: OVERLAY_Z }}
      onClick={closeOnBackdrop ? (e) => { if (e.target === e.currentTarget) onClose(); } : undefined}
    >
      {children}
    </div>,
    document.body,
  );
}
